import { z } from "zod";
import { paymentRequirementSchema } from "./shared";
import { dbTimeSchema } from "./appointments";
import { publicMemberTagsSchema } from "./memberTags";

export const dbDateSchema = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/, {
  message: "date must be in YYYY-MM-DD format",
});

export const dayStatusSchema = z.enum(["vacation", "workday", "offday"]);

export const scheduleOpeningsQuerySchema = z.object({
  scheduleId: z.string().min(1),
  date: dbDateSchema,
  serviceDuration: z.coerce
    .number()
    .int()
    .positive()
    .max(8 * 60),
  /**
   * Comma-separated ids of the services being booked (business services or
   * member clones). When set, slots that start sooner than the largest
   * minimum notice among them (`Service.minBookingNoticeMinutes`) are left
   * out. Optional: without it no notice is applied here, and the commit
   * still refuses a time inside the notice.
   */
  serviceIds: z.string().max(2000).optional(),
});

export const nextAvailabilityQuerySchema = z.object({
  scheduleIds: z.string().min(1),
  salonId: z.string().min(1),
  /**
   * When set, restrict the search to per-member child schedules owned by
   * this team member (mirrors `/v1/openings/members`). The provided
   * `scheduleIds` are treated as parent (business) schedules in this case.
   */
  teamMemberId: z.string().min(1).optional(),
  date: dbDateSchema,
  serviceDuration: z.coerce
    .number()
    .int()
    .positive()
    .max(8 * 60),
  days: z.coerce.number().int().positive().max(120).optional(),
  /**
   * Stop searching once this many dates have an opening (after the minimum
   * notice), returning every schedule open on the last of them. Without it
   * the whole `days` window is read and returned.
   */
  limit: z.coerce.number().int().positive().max(31).optional(),
  /**
   * Comma-separated ids of the services being booked (business services or
   * member clones). When set, slots that start sooner than the largest
   * minimum notice among them (`Service.minBookingNoticeMinutes`) are left
   * out. Optional: without it no notice is applied here, and the commit
   * still refuses a time inside the notice.
   */
  serviceIds: z.string().max(2000).optional(),
});

export const openingsScheduleParkingSchema = z
  .object({
    link: z.string().optional(),
    description: z.string().optional(),
  })
  .nullable()
  .optional();

export const openingsScheduleSchema = z
  .object({
    id: z.string(),
    title: z.string().optional(),
    address: z.string().nullable().optional(),
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
    parking: openingsScheduleParkingSchema,
    hours: z.unknown().optional(),
  })
  .passthrough();

/** @deprecated Schedule now carries address fields directly */
export const openingsScheduleLocationSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().optional(),
    address: z.string().optional(),
    lat: z.number().optional(),
    lng: z.number().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

export const vacationRangeSchema = z.object({
  startDate: dbDateSchema,
  endDate: dbDateSchema,
});

export const openingsAvailabilitySchema = z.object({
  openings: z.array(dbTimeSchema),
  openingLabels: z.record(z.string(), z.string()).optional().default({}),
  dayStatus: dayStatusSchema.optional(),
  /** Populated only when dayStatus is "vacation": the matching vacation range. */
  vacationRange: vacationRangeSchema.nullable().optional(),
  /**
   * IANA timezone the openings/labels were computed in (schedule-level when set,
   * otherwise the business/salon fallback). Lets consumers avoid guessing or
   * falling back to a mismatched salon timezone.
   */
  timezone: z.string().optional(),
});

export const scheduleOpeningsResponseSchema = openingsAvailabilitySchema;

export const openingsItemSchema = openingsAvailabilitySchema.extend({
  schedule: openingsScheduleSchema,
});

export const openingsListResponseSchema = z.array(openingsItemSchema);

export const nextAvailabilityItemSchema = openingsItemSchema.extend({
  date: dbDateSchema,
});

export const nextAvailabilityListResponseSchema = z.array(
  nextAvailabilityItemSchema,
);

export const locationOpeningsServiceOptionSchema = z.object({
  id: z.string(),
  optionId: z.string().optional(),
  referenceId: z.string().optional(),
  title: z.string(),
  description: z.string().optional(),
  duration: z.number(),
  price: z.number(),
});

export const locationOpeningsServiceSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  duration: z.number().optional(),
  price: z.number().optional(),
  referenceId: z.string().optional(),
  hasConsultation: z.boolean().optional(),
  hasPayAdvance: z.boolean().optional(),
  paymentRequirement: paymentRequirementSchema.optional(),
  depositAmount: z.number().nullable().optional(),
  /**
   * Customers may book this service only at least this many minutes before
   * it starts (a member clone carries its business service's value). The
   * member's openings already leave out the starts that are too soon.
   */
  minBookingNoticeMinutes: z.number().int().positive().nullable().optional(),
  up: z.boolean().optional(),
  options: z.array(locationOpeningsServiceOptionSchema).optional(),
  option: locationOpeningsServiceOptionSchema.optional(),
});

export const locationOpeningsMemberSchema = openingsAvailabilitySchema.extend({
  userId: z.string().nullable().optional(),
  teamMemberId: z.string().nullable().optional(),
  username: z.string(),
  name: z.string(),
  photo: z.string().optional(),
  video: z
    .object({
      url: z.string(),
      thumbnail: z.string().optional(),
    })
    .optional(),
  /** True when the member has portfolio photos or a bio to display. */
  hasPortfolio: z.boolean().optional(),
  tags: publicMemberTagsSchema.optional(),
  schedule: openingsScheduleSchema,
  services: z.array(locationOpeningsServiceSchema).optional().default([]),
});

export const locationOpeningsListResponseSchema = z.array(
  locationOpeningsMemberSchema,
);

export const scheduleOpeningsMembersQuerySchema = z.object({
  salonId: z.string().min(1, "salonId is required"),
  scheduleId: z.string().min(1, "scheduleId is required"),
  date: dbDateSchema,
  services: z.string().optional(),
  /**
   * When "true", members on vacation are returned (with empty openings and
   * dayStatus "vacation") instead of being omitted, so consumers can display
   * them. Members on a regular day off ("offday") are always omitted.
   */
  includeVacation: z
    .string()
    .optional()
    .transform((value) => value === "true"),
});
/** @deprecated Use scheduleOpeningsMembersQuerySchema */
export const locationOpeningsMembersQuerySchema =
  scheduleOpeningsMembersQuerySchema;

export const toggleBreakRequestSchema = z.object({
  scheduleId: z.string().min(1),
  date: dbDateSchema,
  time: dbTimeSchema,
});

export const toggleBreakResponseSchema = z.object({
  breakId: z.string().nullable(),
  isActive: z.boolean(),
});

export const toggleExtraSlotRequestSchema = z.object({
  scheduleId: z.string().min(1),
  date: dbDateSchema,
  time: dbTimeSchema,
});

export const toggleExtraSlotResponseSchema = z.object({
  extraSlotId: z.string().nullable(),
  isActive: z.boolean(),
});

export type DbDate = z.infer<typeof dbDateSchema>;
export type DayStatus = z.infer<typeof dayStatusSchema>;
export type ScheduleOpeningsQuery = z.infer<typeof scheduleOpeningsQuerySchema>;
export type NextAvailabilityQuery = z.infer<typeof nextAvailabilityQuerySchema>;
export type OpeningsScheduleLocation = z.infer<
  typeof openingsScheduleLocationSchema
>;
export type OpeningsSchedule = z.infer<typeof openingsScheduleSchema>;
export type OpeningsAvailability = z.infer<typeof openingsAvailabilitySchema>;
export type ScheduleOpeningsResponse = z.infer<
  typeof scheduleOpeningsResponseSchema
>;
export type OpeningsItem = z.infer<typeof openingsItemSchema>;
export type OpeningsListResponse = z.infer<typeof openingsListResponseSchema>;
export type NextAvailabilityItem = z.infer<typeof nextAvailabilityItemSchema>;
export type NextAvailabilityListResponse = z.infer<
  typeof nextAvailabilityListResponseSchema
>;
export type LocationOpeningsService = z.infer<
  typeof locationOpeningsServiceSchema
>;
export type LocationOpeningsServiceOption = z.infer<
  typeof locationOpeningsServiceOptionSchema
>;
export type LocationOpeningsMember = z.infer<
  typeof locationOpeningsMemberSchema
>;
export type LocationOpeningsListResponse = z.infer<
  typeof locationOpeningsListResponseSchema
>;
export type ScheduleOpeningsMembersQuery = z.infer<
  typeof scheduleOpeningsMembersQuerySchema
>;
/** @deprecated Use ScheduleOpeningsMembersQuery */
export type LocationOpeningsMembersQuery = ScheduleOpeningsMembersQuery;
export type ToggleBreakRequest = z.infer<typeof toggleBreakRequestSchema>;
export type ToggleBreakResponse = z.infer<typeof toggleBreakResponseSchema>;
export type ToggleExtraSlotRequest = z.infer<
  typeof toggleExtraSlotRequestSchema
>;
export type ToggleExtraSlotResponse = z.infer<
  typeof toggleExtraSlotResponseSchema
>;
